import { SupportedLanguage } from '../types';

export const LANGUAGES: { code: SupportedLanguage; name: string; nativeName: string; flag: string; rtl: boolean }[] = [
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', rtl: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', rtl: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', rtl: false },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', rtl: false },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', rtl: false },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', rtl: false },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', rtl: false },
];

export function isRTL(lang: SupportedLanguage): boolean {
  return lang === 'ar';
}

export function detectUserLanguage(): SupportedLanguage {
  if (typeof window === 'undefined' || !navigator) return 'en';

  // Check saved language preference first
  try {
    const saved = localStorage.getItem('omnifetch_user_lang');
    if (saved && ['ar', 'en', 'fr', 'es', 'de', 'it', 'pt', 'hi'].includes(saved)) {
      return saved as SupportedLanguage;
    }
  } catch {
    // localStorage unavailable or restricted
  }

  // Fallback to checking navigator.languages or navigator.language
  const userLangs = navigator.languages && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language || (navigator as any).userLanguage || 'en'];

  const supportedCodes: SupportedLanguage[] = ['ar', 'en', 'fr', 'es', 'de', 'it', 'pt', 'hi'];

  for (const lang of userLangs) {
    if (!lang) continue;
    const code = lang.split('-')[0].toLowerCase() as SupportedLanguage;
    if (supportedCodes.includes(code)) {
      return code;
    }
  }

  return 'en';
}

export const uiTranslations: Record<SupportedLanguage, Record<string, string>> = {
  ar: {
    siteTitle: 'OmniFetch Pro - أسرع أداة تحميل فيديوهات مجانية بدون علامة مائية',
    siteSubtitle: 'قم بتحميل الفيديوهات والريلز والمقاطع الصوتية من تيك توك، فيسبوك، إنستغرام، يوتيوب وسناب شات بجودة عالية MP4 & MP3 بنقرة واحدة.',
    heroTitle: 'أداة تحميل الفيديوهات الشاملة السريعة',
    heroTagline: 'ادعم جميع المنصات العالمية | بدون علامة مائية | مجاني 100% وبدون تسجيل',
    inputPlaceholder: 'الصق رابط الفيديو هنا (مثال: TikTok, Instagram, YouTube, Facebook)...',
    pasteBtn: 'لصق',
    downloadBtn: 'تحميل الفيديو',
    processing: 'جاري المعالجة وجلب البيانات...',
    downloading: 'جاري التحميل المباشر...',
    downloadStarted: 'بدأ التحميل بنجاح! يتم حفظ ملف الفيديو/الصوت على جهازك الآن.',
    dropZoneText: 'أو قم بسحب وإسقاط الرابط هنا',
    supportedPlatforms: 'المنصات المدعومة',
    featuresTitle: 'لماذا تختار OmniFetch Pro؟',
    stepsTitle: 'كيفية تحميل الفيديو في 3 خطوات بسيطة',
    faqTitle: 'الأسئلة الشائعة',
    reviewsTitle: 'آراء وتقييمات المستخدمين',
    noWatermark: 'بدون علامة مائية',
    highSpeed: 'سرعة تحويل فائقة',
    unlimited: 'تحميلات غير محدودة',
    safeSecure: 'آمن وخصوصية مضمونة',
    copyLink: 'نسخ الرابط',
    copied: 'تم النسخ!',
    qrCode: 'رمز QR للموبايل',
    share: 'مشاركة',
    exportToCloud: 'حفظ في السحابة',
    exportCloudModalTitle: 'تصدير الفيديو إلى الحسابات السحابية',
    exportCloudModalDesc: 'احفظ الفيديو مباشرة في حسابك على Google Drive أو Dropbox دون الحاجة لتنزيله محلياً أولاً.',
    saveToGoogleDrive: 'حفظ في Google Drive',
    saveToDropbox: 'حفظ في Dropbox',
    saveToOneDrive: 'حفظ في OneDrive',
    copyDirectCloudUrl: 'نسخ رابط التنزيل المباشر للسحابة',
    preparingCloudSave: 'جاري التوجيه إلى الخدمة السحابية...',
    autoPlayToggle: 'التشغيل التلقائي للمقطع',
    processingVideo: 'جاري المعالجة والتحضير...',
    downloadingProgress: 'جاري تنزيل الملف...',
    etaRemaining: 'الوقت المتبقي',
    speedLabel: 'السرعة',
    shareResult: 'شارك الفيديو مع أصدقائك:',
    shareWhatsApp: 'واتساب',
    shareTelegram: 'تليجرام',
    shareTwitter: 'تويتر (X)',
    shareFacebook: 'فيسبوك',
    shareNative: 'مشاركة سريعة',
    downloadMp4: 'تحميل MP4',
    downloadHd: 'تحميل HD (1080p)',
    download4k: 'تحميل 4K Ultra HD',
    downloadAudio: 'تحميل الصوت MP3',
    downloadHistory: 'سجل التحميلات',
    recentDownloads: 'آخر التحميلات الخاصة بك',
    clearHistory: 'مسح السجل',
    noHistory: 'لا يوجد أي تحميلات سابقة حتى الآن.',
    adminDashboard: 'لوحة التحكم',
    blog: 'المدونة والتعليمات',
    allPlatforms: 'جميع المنصات',
    close: 'إغلاق',
    installPwa: 'تثبيت التطبيق على جهازك',
    pwaDesc: 'قم بتثبيت OmniFetch Pro على هاتفك أو حاسوبك للوصول السريع بدون متصفح.',
    install: 'تثبيت الآن',
    footerRights: 'جميع الحقوق محفوظة © 2026 OmniFetch Pro.',
    privacyPolicy: 'سياسة الخصوصية',
    termsOfService: 'شروط الخدمة',
    dmca: 'سياسة حقوق النشر DMCA',
    disclaimer: 'إخلاء المسؤولية',
    cookiePolicy: 'سياسة ملفات تعريف الارتباط',
    aboutUs: 'من نحن',
    contactUs: 'اتصل بنا',
    adSpace: 'مساحة إعلانية مخصصة (AdSense Ready)',
    errorInvalidUrl: 'يرجى إدخال رابط فيديو صحيح ومنصة مدعومة.',
    errorFetchFailed: 'تعذر جلب الفيديو. يرجى التأكد من أن الرابط عام وليس خاصًا ثم المحاولة مجددًا.',
    quality: 'الجودة',
    format: 'الصيغة',
    size: 'الحجم',
    duration: 'المدة',
    creator: 'صانع المحتوى',
    views: 'المشاهدات',
    scanQrNotice: 'امسح رمز QR باستخدام كاميرا هاتفك للتحميل المباشر على جوالك.',
    contactTitle: 'تواصل مع فريق الدعم',
    contactDesc: 'هل لديك استفسار أو اقتراح أو بلاغ عن انتهاك حقوق ملكية؟ تواصل معنا فورًا.',
    nameLabel: 'الاسم الكامل',
    emailLabel: 'البريد الإلكتروني',
    messageLabel: 'الرسالة',
    sendMsg: 'إرسال الرسالة',
    msgSentSuccess: 'تم إرسال رسالتك بنجاح! سنرد عليك في أقرب وقت.',
    step1Title: '1. اقطع أو انسخ الرابط',
    step1Desc: 'افتح التطبيق أو المنصة وانسخ رابط الفيديو أو الريل المفضل لديك.',
    step2Title: '2. الصق الرابط هنا',
    step2Desc: 'ضع الرابط في مربع البحث أعلى الصفحة واضغط على زر "تحميل".',
    step3Title: '3. اختر الجودة واحفظ',
    step3Desc: 'اختر الجودة المناسبة (HD, 4K, MP3) واضغط على زر التحميل المباشر.',
    trustpilotReviewNudge: 'هل أعجبك OmniFetch؟ اترك لنا تقييماً سريعاً 5 نجوم على Trustpilot! ⭐',
  },
  en: {
    siteTitle: 'OmniFetch Pro - Fastest Free Online Video Downloader Without Watermark',
    siteSubtitle: 'Download HD videos, reels, and audio from TikTok, Facebook, Instagram, YouTube, and Snapchat in MP4 & MP3 with one click.',
    heroTitle: 'Universal High-Speed Video Downloader',
    heroTagline: 'Supports All Social Media Platforms | No Watermark | 100% Free & No Registration',
    inputPlaceholder: 'Paste video link here (e.g. TikTok, Instagram, YouTube, Facebook)...',
    pasteBtn: 'Paste',
    downloadBtn: 'Download Video',
    processing: 'Fetching media & extracting formats...',
    dropZoneText: 'Or drag & drop link right here',
    supportedPlatforms: 'Supported Platforms',
    featuresTitle: 'Why Choose OmniFetch Pro?',
    stepsTitle: 'How to Download Videos in 3 Simple Steps',
    faqTitle: 'Frequently Asked Questions',
    reviewsTitle: 'User Reviews & Ratings',
    noWatermark: 'No Watermark',
    highSpeed: 'Lightning Fast Conversion',
    unlimited: 'Unlimited Free Downloads',
    safeSecure: '100% Safe & Private',
    copyLink: 'Copy Link',
    copied: 'Copied!',
    qrCode: 'Mobile QR Code',
    share: 'Share',
    exportToCloud: 'Export to Cloud',
    exportCloudModalTitle: 'Export Video to Cloud Storage',
    exportCloudModalDesc: 'Save this video directly to your Google Drive, Dropbox, or OneDrive account without downloading it locally.',
    saveToGoogleDrive: 'Save to Google Drive',
    saveToDropbox: 'Save to Dropbox',
    saveToOneDrive: 'Save to OneDrive',
    copyDirectCloudUrl: 'Copy Direct Stream URL for Cloud',
    preparingCloudSave: 'Redirecting to cloud service...',
    autoPlayToggle: 'Auto-play video',
    processingVideo: 'Processing & preparing video...',
    downloadingProgress: 'Downloading file...',
    etaRemaining: 'ETA',
    speedLabel: 'Speed',
    shareResult: 'Share video with friends:',
    shareWhatsApp: 'WhatsApp',
    shareTelegram: 'Telegram',
    shareTwitter: 'Twitter (X)',
    shareFacebook: 'Facebook',
    shareNative: 'Quick Share',
    downloadMp4: 'Download MP4',
    downloadHd: 'Download HD (1080p)',
    download4k: 'Download 4K Ultra HD',
    downloadAudio: 'Download Audio (MP3)',
    downloadHistory: 'Download History',
    recentDownloads: 'Your Recent Downloads',
    clearHistory: 'Clear History',
    noHistory: 'No recent downloads stored yet.',
    adminDashboard: 'Admin Dashboard',
    blog: 'Blog & Tutorials',
    allPlatforms: 'All Platforms',
    close: 'Close',
    installPwa: 'Install App on Your Device',
    pwaDesc: 'Install OmniFetch Pro for instant 1-click access right from your desktop or home screen.',
    install: 'Install Now',
    footerRights: 'All Rights Reserved © 2026 OmniFetch Pro.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    dmca: 'DMCA & Copyright Policy',
    disclaimer: 'Disclaimer',
    cookiePolicy: 'Cookie Policy',
    aboutUs: 'About Us',
    contactUs: 'Contact Us',
    adSpace: 'Ad Placement Zone (AdSense Ready)',
    errorInvalidUrl: 'Please enter a valid video link from a supported platform.',
    errorFetchFailed: 'Failed to extract video data. Please verify the URL is public and try again.',
    quality: 'Quality',
    format: 'Format',
    size: 'Size',
    duration: 'Duration',
    creator: 'Creator',
    views: 'Views',
    scanQrNotice: 'Scan this QR code with your mobile camera to download directly to your phone.',
    contactTitle: 'Contact Our Support Team',
    contactDesc: 'Have questions, suggestions or copyright inquiries? Get in touch with us anytime.',
    nameLabel: 'Full Name',
    emailLabel: 'Email Address',
    messageLabel: 'Your Message',
    sendMsg: 'Send Message',
    msgSentSuccess: 'Your message has been sent successfully! We will get back to you shortly.',
    step1Title: '1. Copy Video URL',
    step1Desc: 'Open your favorite video app or website and copy the shareable video link.',
    step2Title: '2. Paste in OmniFetch',
    step2Desc: 'Insert the link in our search box above and hit the "Download" button.',
    step3Title: '3. Save in HD / MP3',
    step3Desc: 'Select your preferred format (HD, 4K, MP3) and download directly to your device.',
    trustpilotReviewNudge: 'Happy with OmniFetch? Leave a quick 5-star review on Trustpilot! ⭐',
  },
  fr: {
    siteTitle: 'OmniFetch Pro - Téléchargeur de vidéos gratuit et rapide sans filigrane',
    siteSubtitle: 'Téléchargez des vidéos HD, reels et fichiers audio depuis TikTok, Facebook, Instagram, YouTube et Snapchat en un clic.',
    heroTitle: 'Téléchargeur de Vidéos Universel Rapide',
    heroTagline: 'Toutes plateformes supportées | Sans filigrane | 100% Gratuit',
    inputPlaceholder: 'Collez le lien de la vidéo ici...',
    pasteBtn: 'Coller',
    downloadBtn: 'Télécharger',
    processing: 'Traitement de la vidéo en cours...',
    dropZoneText: 'Ou glissez-déposez le lien ici',
    supportedPlatforms: 'Plateformes Supportées',
    featuresTitle: 'Pourquoi choisir OmniFetch Pro ?',
    stepsTitle: 'Comment télécharger en 3 étapes simples',
    faqTitle: 'Foire Aux Questions',
    reviewsTitle: 'Avis des utilisateurs',
    noWatermark: 'Sans Filigrane',
    highSpeed: 'Conversion Ultra Rapide',
    unlimited: 'Téléchargements Illimités',
    safeSecure: '100% Sécurisé',
    copyLink: 'Copier le lien',
    copied: 'Copié !',
    qrCode: 'Code QR Mobile',
    share: 'Partager',
    exportToCloud: 'Exporter vers le Cloud',
    exportCloudModalTitle: 'Exporter vers le stockage Cloud',
    exportCloudModalDesc: 'Enregistrez cette vidéo directement sur Google Drive, Dropbox ou OneDrive.',
    saveToGoogleDrive: 'Enregistrer sur Google Drive',
    saveToDropbox: 'Enregistrer sur Dropbox',
    saveToOneDrive: 'Enregistrer sur OneDrive',
    copyDirectCloudUrl: 'Copier le lien direct pour le Cloud',
    preparingCloudSave: 'Redirection vers le service Cloud...',
    autoPlayToggle: 'Lecture automatique',
    processingVideo: 'Traitement de la vidéo...',
    downloadingProgress: 'Téléchargement du fichier...',
    etaRemaining: 'Temps restant',
    speedLabel: 'Vitesse',
    downloadMp4: 'Télécharger MP4',
    downloadHd: 'Télécharger HD',
    download4k: 'Télécharger 4K',
    downloadAudio: 'Télécharger Audio MP3',
    downloadHistory: 'Historique',
    recentDownloads: 'Vos récents téléchargements',
    clearHistory: 'Effacer l\'historique',
    noHistory: 'Aucun téléchargement récent.',
    adminDashboard: 'Tableau de bord Admin',
    blog: 'Blog',
    allPlatforms: 'Toutes les plateformes',
    close: 'Fermer',
    installPwa: 'Installer l\'application',
    pwaDesc: 'Installez OmniFetch Pro sur votre appareil pour un accès instantané.',
    install: 'Installer',
    footerRights: 'Tous droits réservés © 2026 OmniFetch Pro.',
    privacyPolicy: 'Politique de confidentialité',
    termsOfService: 'Conditions d\'utilisation',
    dmca: 'Politique DMCA',
    disclaimer: 'Avertissement',
    cookiePolicy: 'Politique des cookies',
    aboutUs: 'À propos',
    contactUs: 'Contactez-nous',
    adSpace: 'Espace Publicitaire (AdSense Ready)',
    errorInvalidUrl: 'Veuillez entrer un lien de vidéo valide.',
    errorFetchFailed: 'Échec de l\'extraction de la vidéo. Vérifiez que le lien est public.',
    quality: 'Qualité',
    format: 'Format',
    size: 'Taille',
    duration: 'Durée',
    creator: 'Créateur',
    views: 'Vues',
    scanQrNotice: 'Scannez ce QR code avec votre téléphone pour télécharger directement.',
    contactTitle: 'Contactez le support',
    contactDesc: 'Une question ou une demande ? Écrivez-nous.',
    nameLabel: 'Nom complet',
    emailLabel: 'Adresse e-mail',
    messageLabel: 'Message',
    sendMsg: 'Envoyer',
    msgSentSuccess: 'Message envoyé avec succès !',
    step1Title: '1. Copiez le lien',
    step1Desc: 'Ouvrez l\'application et copiez le lien de la vidéo.',
    step2Title: '2. Collez le lien',
    step2Desc: 'Insérez le lien dans notre champ de recherche.',
    step3Title: '3. Téléchargez',
    step3Desc: 'Choisissez la qualité et enregistrez le fichier.',
  },
  es: {
    siteTitle: 'OmniFetch Pro - Descargador de videos gratis y rápido sin marca de agua',
    siteSubtitle: 'Descarga videos HD, reels y audio de TikTok, Facebook, Instagram, YouTube y Snapchat en MP4 y MP3.',
    heroTitle: 'Descargador de Videos Universal Ultrarrápido',
    heroTagline: 'Soporta todas las redes sociales | Sin marca de agua | 100% Gratis',
    inputPlaceholder: 'Pega el enlace del video aquí...',
    pasteBtn: 'Pegar',
    downloadBtn: 'Descargar',
    processing: 'Procesando el enlace...',
    dropZoneText: 'O arrastra y suelta el enlace aquí',
    supportedPlatforms: 'Plataformas Soportadas',
    featuresTitle: '¿Por qué elegir OmniFetch Pro?',
    stepsTitle: 'Cómo descargar en 3 sencillos pasos',
    faqTitle: 'Preguntas Frecuentes',
    reviewsTitle: 'Opiniones de usuarios',
    noWatermark: 'Sin Marca de Agua',
    highSpeed: 'Conversión Ultrarrápida',
    unlimited: 'Descargas Ilimitadas',
    safeSecure: '100% Seguro y Privado',
    copyLink: 'Copiar enlace',
    copied: '¡Copiado!',
    qrCode: 'Código QR Móvil',
    share: 'Compartir',
    exportToCloud: 'Exportar a la Nube',
    exportCloudModalTitle: 'Exportar video a almacenamiento en la Nube',
    exportCloudModalDesc: 'Guarda este video directamente en tu cuenta de Google Drive, Dropbox o OneDrive.',
    saveToGoogleDrive: 'Guardar en Google Drive',
    saveToDropbox: 'Guardar en Dropbox',
    saveToOneDrive: 'Guardar en OneDrive',
    copyDirectCloudUrl: 'Copiar enlace directo para la nube',
    preparingCloudSave: 'Redirigiendo al servicio en la nube...',
    autoPlayToggle: 'Reproducción automática',
    processingVideo: 'Procesando el video...',
    downloadingProgress: 'Descargando archivo...',
    etaRemaining: 'Tiempo restante',
    speedLabel: 'Velocidad',
    downloadMp4: 'Descargar MP4',
    downloadHd: 'Descargar HD',
    download4k: 'Descargar 4K',
    downloadAudio: 'Descargar Audio MP3',
    downloadHistory: 'Historial',
    recentDownloads: 'Tus descargas recientes',
    clearHistory: 'Limpiar historial',
    noHistory: 'No hay descargas recientes.',
    adminDashboard: 'Panel de Control',
    blog: 'Blog',
    allPlatforms: 'Todas las plataformas',
    close: 'Cerrar',
    installPwa: 'Instalar Aplicación',
    pwaDesc: 'Instala OmniFetch Pro en tu dispositivo para acceso directo.',
    install: 'Instalar',
    footerRights: 'Todos los derechos reservados © 2026 OmniFetch Pro.',
    privacyPolicy: 'Política de Privacidad',
    termsOfService: 'Términos de Servicio',
    dmca: 'Política DMCA',
    disclaimer: 'Aviso Legal',
    cookiePolicy: 'Política de Cookies',
    aboutUs: 'Sobre Nosotros',
    contactUs: 'Contacto',
    adSpace: 'Espacio Publicitario (AdSense Ready)',
    errorInvalidUrl: 'Por favor, ingresa un enlace válido.',
    errorFetchFailed: 'No se pudo obtener el video. Revisa que el video sea público.',
    quality: 'Calidad',
    format: 'Formato',
    size: 'Tamaño',
    duration: 'Duración',
    creator: 'Creador',
    views: 'Vistas',
    scanQrNotice: 'Escanea el código QR con tu celular para descargar directo.',
    contactTitle: 'Contacto de Soporte',
    contactDesc: '¿Tienes dudas o comentarios? Escríbenos.',
    nameLabel: 'Nombre Completo',
    emailLabel: 'Correo Electrónico',
    messageLabel: 'Mensaje',
    sendMsg: 'Enviar Mensaje',
    msgSentSuccess: '¡Mensaje enviado con éxito!',
    step1Title: '1. Copia el enlace',
    step1Desc: 'Abre la app o sitio y copia el enlace del video.',
    step2Title: '2. Pega el enlace',
    step2Desc: 'Pega el enlace en la barra superior.',
    step3Title: '3. Descarga en HD',
    step3Desc: 'Elige la calidad y guarda tu video.',
  },
  de: {
    siteTitle: 'OmniFetch Pro - Schneller kostenloser Video Downloader ohne Wasserzeichen',
    siteSubtitle: 'Laden Sie HD-Videos, Reels und MP3-Audio von TikTok, Facebook, Instagram, YouTube und Snapchat herunter.',
    heroTitle: 'Universeller High-Speed Video Downloader',
    heroTagline: 'Unterstützt alle Plattformen | Ohne Wasserzeichen | 100% Kostenlos',
    inputPlaceholder: 'Video-Link hier einfügen...',
    pasteBtn: 'Einfügen',
    downloadBtn: 'Herunterladen',
    processing: 'Medien werden verarbeitet...',
    dropZoneText: 'Oder Link hierher ziehen',
    supportedPlatforms: 'Unterstützte Plattformen',
    featuresTitle: 'Warum OmniFetch Pro wählen?',
    stepsTitle: 'In 3 einfachen Schritten herunterladen',
    faqTitle: 'Häufig gestellte Fragen',
    reviewsTitle: 'Bewertungen',
    noWatermark: 'Ohne Wasserzeichen',
    highSpeed: 'Blitzschnelle Konvertierung',
    unlimited: 'Unbegrenzte Downloads',
    safeSecure: '100% Sicher & Datenschutz',
    copyLink: 'Link kopieren',
    copied: 'Kopiert!',
    qrCode: 'Mobile QR-Code',
    share: 'Teilen',
    exportToCloud: 'In Cloud exportieren',
    exportCloudModalTitle: 'Video in Cloud-Speicher exportieren',
    exportCloudModalDesc: 'Speichern Sie dieses Video direkt in Ihrem Google Drive-, Dropbox- oder OneDrive-Konto.',
    saveToGoogleDrive: 'In Google Drive speichern',
    saveToDropbox: 'In Dropbox speichern',
    saveToOneDrive: 'In OneDrive speichern',
    copyDirectCloudUrl: 'Direkten Stream-Link kopieren',
    preparingCloudSave: 'Weiterleitung zum Cloud-Dienst...',
    autoPlayToggle: 'Automatische Wiedergabe',
    processingVideo: 'Video wird verarbeitet...',
    downloadingProgress: 'Datei wird heruntergeladen...',
    etaRemaining: 'Verbleibende Zeit',
    speedLabel: 'Geschwindigkeit',
    downloadMp4: 'Download MP4',
    downloadHd: 'Download HD',
    download4k: 'Download 4K',
    downloadAudio: 'Download Audio MP3',
    downloadHistory: 'Verlauf',
    recentDownloads: 'Ihre letzten Downloads',
    clearHistory: 'Verlauf löschen',
    noHistory: 'Noch keine Downloads gespeichert.',
    adminDashboard: 'Admin Dashboard',
    blog: 'Blog',
    allPlatforms: 'Alle Plattformen',
    close: 'Schließen',
    installPwa: 'App installieren',
    pwaDesc: 'Installieren Sie OmniFetch Pro für den schnellen Zugriff.',
    install: 'Jetzt installieren',
    footerRights: 'Alle Rechte vorbehalten © 2026 OmniFetch Pro.',
    privacyPolicy: 'Datenschutz-Bestimmungen',
    termsOfService: 'Nutzungsbedingungen',
    dmca: 'DMCA-Richtlinie',
    disclaimer: 'Haftungsausschluss',
    cookiePolicy: 'Cookie-Richtlinie',
    aboutUs: 'Über uns',
    contactUs: 'Kontakt',
    adSpace: 'Werbefläche (AdSense Bereit)',
    errorInvalidUrl: 'Bitte geben Sie einen gültigen Link ein.',
    errorFetchFailed: 'Videodaten konnten nicht geladen werden.',
    quality: 'Qualität',
    format: 'Format',
    size: 'Größe',
    duration: 'Dauer',
    creator: 'Ersteller',
    views: 'Aufrufe',
    scanQrNotice: 'Scannen Sie den QR-Code mit Ihrem Smartphone.',
    contactTitle: 'Support kontaktieren',
    contactDesc: 'Haben Sie Fragen? Schreiben Sie uns.',
    nameLabel: 'Vollständiger Name',
    emailLabel: 'E-Mail-Adresse',
    messageLabel: 'Nachricht',
    sendMsg: 'Nachricht senden',
    msgSentSuccess: 'Nachricht erfolgreich gesendet!',
    step1Title: '1. Link kopieren',
    step1Desc: 'Kopieren Sie den Link des gewünschten Videos.',
    step2Title: '2. Link einfügen',
    step2Desc: 'Fügen Sie den Link in das Suchfeld ein.',
    step3Title: '3. Herunterladen',
    step3Desc: 'Wählen Sie die Qualität und speichern Sie die Datei.',
  },
  it: {
    siteTitle: 'OmniFetch Pro - Downloader Video Gratuito e Veloce Senza Watermark',
    siteSubtitle: 'Scarica video HD, reels e audio MP3 da TikTok, Facebook, Instagram, YouTube e Snapchat con un solo clic.',
    heroTitle: 'Downloader Video Universale Ad Alta Velocità',
    heroTagline: 'Supporta tutte le piattaforme | Senza filigrana | 100% Gratuito',
    inputPlaceholder: 'Incolla qui il link del video...',
    pasteBtn: 'Incolla',
    downloadBtn: 'Scarica',
    processing: 'Elaborazione del video in corso...',
    dropZoneText: 'Oppure trascina e rilascia il link qui',
    supportedPlatforms: 'Piattaforme Supportate',
    featuresTitle: 'Perché scegliere OmniFetch Pro?',
    stepsTitle: 'Come scaricare in 3 semplici passaggi',
    faqTitle: 'Domande Frequenti',
    reviewsTitle: 'Recensioni degli utenti',
    noWatermark: 'Senza Filigrana',
    highSpeed: 'Conversione Ultra Veloce',
    unlimited: 'Download Illimitati',
    safeSecure: '100% Sicuro e Protetto',
    copyLink: 'Copia Link',
    copied: 'Copiato!',
    qrCode: 'Codice QR Mobile',
    share: 'Condividi',
    exportToCloud: 'Esporta nel Cloud',
    exportCloudModalTitle: 'Esporta video nel Cloud Storage',
    exportCloudModalDesc: 'Salva questo video direttamente nel tuo account Google Drive, Dropbox o OneDrive.',
    saveToGoogleDrive: 'Salva su Google Drive',
    saveToDropbox: 'Salva su Dropbox',
    saveToOneDrive: 'Salva su OneDrive',
    copyDirectCloudUrl: 'Copia URL diretto per Cloud',
    preparingCloudSave: 'Reindirizzamento al servizio cloud...',
    autoPlayToggle: 'Riproduzione automatica',
    processingVideo: 'Elaborazione del video...',
    downloadingProgress: 'Download del file...',
    etaRemaining: 'Tempo rimasto',
    speedLabel: 'Velocità',
    downloadMp4: 'Scarica MP4',
    downloadHd: 'Scarica HD',
    download4k: 'Scarica 4K',
    downloadAudio: 'Scarica Audio MP3',
    downloadHistory: 'Cronologia',
    recentDownloads: 'I tuoi download recenti',
    clearHistory: 'Cancella Cronologia',
    noHistory: 'Nessun download recente memorizzato.',
    adminDashboard: 'Pannello di Controllo',
    blog: 'Blog',
    allPlatforms: 'Tutte le piattaforme',
    close: 'Chiudi',
    installPwa: 'Installa Applicazione',
    pwaDesc: 'Installa OmniFetch Pro sul tuo dispositivo per un accesso immediato.',
    install: 'Installa Ora',
    footerRights: 'Tutti i diritti riservati © 2026 OmniFetch Pro.',
    privacyPolicy: 'Informativa sulla Privacy',
    termsOfService: 'Termini di Servizio',
    dmca: 'Politica DMCA',
    disclaimer: 'Dichiarazione di non responsabilità',
    cookiePolicy: 'Politica sui Cookie',
    aboutUs: 'Chi Siamo',
    contactUs: 'Contattaci',
    adSpace: 'Spazio Pubblicitario (Pronto per AdSense)',
    errorInvalidUrl: 'Inserisci un link video valido.',
    errorFetchFailed: 'Impossibile estrarre il video. Verifica che sia pubblico.',
    quality: 'Qualità',
    format: 'Formato',
    size: 'Dimensione',
    duration: 'Durata',
    creator: 'Creatore',
    views: 'Visualizzazioni',
    scanQrNotice: 'Scansiona il codice QR con il tuo smartphone.',
    contactTitle: 'Contatta il Supporto',
    contactDesc: 'Hai domande? Scrivici in qualsiasi momento.',
    nameLabel: 'Nome Completo',
    emailLabel: 'Indirizzo Email',
    messageLabel: 'Messaggio',
    sendMsg: 'Invia Messaggio',
    msgSentSuccess: 'Messaggio inviato con successo!',
    step1Title: '1. Copia il link',
    step1Desc: 'Copia il link del video che vuoi scaricare.',
    step2Title: '2. Incolla il link',
    step2Desc: 'Incolla il link nel campo di ricerca.',
    step3Title: '3. Scarica',
    step3Desc: 'Scegli la qualità e salva il file.',
    trustpilotReviewNudge: 'Ti piace OmniFetch? Lascia una rapida recensione a 5 stelle su Trustpilot! ⭐',
  },
  pt: {
    siteTitle: 'OmniFetch Pro - Baixador de Vídeos Grátis Sem Marca d\'Água',
    siteSubtitle: 'Baixe vídeos em HD, reels e áudios do TikTok, Facebook, Instagram, YouTube e Snapchat em MP4 e MP3 com um clique.',
    heroTitle: 'Baixador de Vídeos Universal de Alta Velocidade',
    heroTagline: 'Suporta Todas as Plataformas | Sem Marca d\'Água | 100% Grátis',
    inputPlaceholder: 'Cole o link do vídeo aqui (ex: TikTok, Instagram, YouTube, Facebook)...',
    pasteBtn: 'Colar',
    downloadBtn: 'Baixar Vídeo',
    processing: 'Buscando mídia e extraindo formatos...',
    dropZoneText: 'Ou arraste e solte o link aqui',
    supportedPlatforms: 'Plataformas Suportadas',
    featuresTitle: 'Por que escolher o OmniFetch Pro?',
    stepsTitle: 'Como Baixar Vídeos em 3 Passos Simples',
    faqTitle: 'Perguntas Frequentes',
    reviewsTitle: 'Avaliações dos Usuários',
    noWatermark: 'Sem Marca d\'Água',
    highSpeed: 'Conversão Ultra Rápida',
    unlimited: 'Downloads Ilimitados',
    safeSecure: '100% Seguro e Privado',
    copyLink: 'Copiar Link',
    copied: 'Copiado!',
    qrCode: 'Código QR Mobile',
    share: 'Compartilhar',
    exportToCloud: 'Exportar para Nuvem',
    downloadMp4: 'Baixar MP4',
    downloadHd: 'Baixar HD',
    download4k: 'Baixar 4K',
    downloadAudio: 'Baixar Áudio MP3',
    downloadHistory: 'Histórico de Downloads',
    recentDownloads: 'Downloads Recentes',
    clearHistory: 'Limpar Histórico',
    noHistory: 'Nenhum download recente.',
    adminDashboard: 'Painel do Administrador',
    blog: 'Blog',
    allPlatforms: 'Todas as Plataformas',
    close: 'Fechar',
    installPwa: 'Instalar Aplicativo',
    pwaDesc: 'Instale o OmniFetch Pro no seu dispositivo para acesso rápido.',
    install: 'Instalar Agora',
    footerRights: 'Todos os direitos reservados © 2026 OmniFetch Pro.',
    privacyPolicy: 'Política de Privacidade',
    termsOfService: 'Termos de Serviço',
    dmca: 'Política DMCA',
    disclaimer: 'Isenção de Responsabilidade',
    cookiePolicy: 'Política de Cookies',
    aboutUs: 'Sobre Nós',
    contactUs: 'Fale Conosco',
    trustpilotReviewNudge: 'Gostou do OmniFetch? Deixe uma avaliação rápida de 5 estrelas no Trustpilot! ⭐',
  },
  hi: {
    siteTitle: 'OmniFetch Pro - वाटरमार्क के बिना सबसे तेज मुफ़्त ऑनलाइन वीडियो डाउनलोडर',
    siteSubtitle: 'TikTok, Facebook, Instagram, YouTube और Snapchat से HD वीडियो, रील्स और MP3 ऑडियो एक क्लिक में डाउनलोड करें।',
    heroTitle: 'यूनिवर्सल हाई-स्पीड वीडियो डाउनलोडर',
    heroTagline: 'सभी सोशल मीडिया प्लेटफॉर्म समर्थित | बिना वाटरमार्क | 100% मुफ़्त',
    inputPlaceholder: 'यहाँ वीडियो लिंक पेस्ट करें (जैसे TikTok, Instagram, YouTube, Facebook)...',
    pasteBtn: 'पेस्ट करें',
    downloadBtn: 'वीडियो डाउनलोड करें',
    processing: 'मीडिया फॉर्मेट खोजा जा रहा है...',
    dropZoneText: 'या लिंक यहाँ ड्रैग और ड्रॉप करें',
    supportedPlatforms: 'समर्थित प्लेटफॉर्म',
    featuresTitle: 'OmniFetch Pro क्यों चुनें?',
    stepsTitle: '3 आसान चरणों में वीडियो कैसे डाउनलोड करें',
    faqTitle: 'अक्सर पूछे जाने वाले प्रश्न',
    reviewsTitle: 'उपयोगकर्ता समीक्षाएं',
    noWatermark: 'बिना वाटरमार्क',
    highSpeed: 'सुपर-फास्ट रूपांतरण',
    unlimited: 'अनलिमिटेड मुफ़्त डाउनलोड',
    safeSecure: '100% सुरक्षित एवं निजी',
    copyLink: 'लिंक कॉपी करें',
    copied: 'कॉपी हो गया!',
    qrCode: 'मोबाइल क्यूआर कोड',
    share: 'शेयर करें',
    exportToCloud: 'क्लाउड में सेव करें',
    downloadMp4: 'MP4 डाउनलोड करें',
    downloadHd: 'HD डाउनलोड करें',
    download4k: '4K डाउनलोड करें',
    downloadAudio: 'MP3 ऑडियो डाउनलोड करें',
    downloadHistory: 'डाउनलोड इतिहास',
    recentDownloads: 'हाल के डाउनलोड',
    clearHistory: 'इतिहास साफ़ करें',
    noHistory: 'अभी तक कोई डाउनलोड नहीं।',
    adminDashboard: 'एडमिन डैशबोर्ड',
    blog: 'ब्लॉग',
    allPlatforms: 'सभी प्लेटफॉर्म',
    close: 'बंद करें',
    installPwa: 'ऐप इंस्टॉल करें',
    pwaDesc: 'तुरंत एक्सेस के लिए अपने डिवाइस पर OmniFetch Pro इंस्टॉल करें।',
    install: 'अभी इंस्टॉल करें',
    footerRights: 'सर्वाधिकार सुरक्षित © 2026 OmniFetch Pro।',
    privacyPolicy: 'गोपनीयता नीति',
    termsOfService: 'सेवा की शर्तें',
    dmca: 'DMCA नीति',
    disclaimer: 'अस्वीकरण',
    cookiePolicy: 'कुकी नीति',
    aboutUs: 'हमारे बारे में',
    contactUs: 'संपर्क करें',
    trustpilotReviewNudge: 'क्या आपको OmniFetch पसंद आया? Trustpilot पर त्वरित 5-स्टार समीक्षा छोड़ें! ⭐',
  }
};

export function t(key: string, lang: SupportedLanguage): string {
  if (uiTranslations[lang] && uiTranslations[lang][key]) {
    return uiTranslations[lang][key];
  }
  if (uiTranslations['en'] && uiTranslations['en'][key]) {
    return uiTranslations['en'][key];
  }
  return key;
}
